import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { Collaboration, Comment, Participant, Upload } from '@range.io/basic-types'
import { mergeRight } from '@range.io/functional'
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 } from 'uuid'
import { useKeyMaps } from '../components-reusable/hooks/index.js'
import { URL_SEARCH_ORDER, useImageUrl } from '../components-reusable/hooks/useImageUrl.js'
import { useNavigateToCollaboration } from '../components-reusable/hooks/useNavigateToCollaboration.js'
import {
    Box,
    Button,
    Flex,
    FlexColumn,
    FlexRow,
    Icon,
    LoadingDotsAnimation,
    TextAreaExternallyChangeable,
} from '../components-reusable/index.js'
import { MediaIconFullscreen } from '../components-reusable/MediaIcon.js'
import {
    CommentAddedCommand,
    CommentChangedCommand,
    CommentRemovedCommand,
    UploadAddedCommand,
    UploadChangedCommand,
    UploadRemovedCommand,
} from '../firebase/commands/index.js'
import { useCommandHistory } from '../firebase/commands/UndoRedo.js'
import { downloadImage, getImageHeightToWidth } from '../helpers.js'
import { styled } from '../range-theme/index.js'
import { ReduxActions, ReduxSelectors } from '../redux/index.js'
import * as Segment from '../segment/segment.js'
import CommentBox from './CommentBox.js'
import CommentFeedItem from './CommentFeedItem.js'
import Identifier from './Identifier.js'
import MediaDetailsViewLocationTile from './MediaDetailsViewLocationTile.js'
import MediaDetailsViewTopBar from './MediaDetailsViewTopBar.js'
import PhotoAnnotationEditor from './PhotoAnnotationEditor.js'
import Tags from './Tags.js'

const StyledMediaDetailsViewContainer = styled(Flex, {
    position: 'absolute',
    width: '100vw',
    height: '100vh',
    top: 0,
    left: 0,
    zIndex: 20,
    backgroundColor: '$neutral03',
})

const StyledMediaDetailsViewSidebar = styled('div', {
    width: '450px',
    minWidth: '450px',
    backgroundColor: '$neutral09',
    borderLeft: '1px solid $neutral07',
    display: 'flex',
    flexDirection: 'column',
})

const StyledSidebarContent = styled(ScrollAreaPrimitive.Root, {
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    variants: {
        shouldShrink: {
            true: { height: 'calc(100% - 64px - 120px)' },
            false: { height: 'calc(100% - 64px - 71px)' },
        },
    },
})

const StyledSidebarContentViewport = styled(ScrollAreaPrimitive.Viewport, {
    width: '100%',
    height: '100%',
    borderRadius: 'inherit',
    flexGrow: '5',
})

const StyledSidebarContentScrollbar = styled(ScrollAreaPrimitive.Scrollbar, {
    width: '4px',
    display: 'flex',
    padding: 2,
    background: 'none',
    // ensures no selection
    userSelect: 'none',
    // disable browser handling of all panning and zooming gestures on touch devices
    touchAction: 'none',
    transition: 'background 160ms ease-out',
})

const StyledSidebarContentScrollbarThumb = styled(ScrollAreaPrimitive.Thumb, {
    flex: 1,
    background: '#99999950',
    borderRadius: '4px',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        height: '100%',
        minWidth: 44,
        minHeight: 44,
    },
})

const StyledImg = styled('img', {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    backgroundColor: '$snapshotBackground',
    variants: {
        isLoading: {
            true: { display: 'none' },
            false: { display: 'visible' },
        },
    },
})

const StyledNavButton = styled('div', {
    width: '52px',
    height: '52px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '100px',
    cursor: 'pointer',
    transitionDuration: '0.2s',
    pointerEvents: 'auto',

    variants: {
        disabled: {
            true: {
                pointerEvents: 'none',
                opacity: 0.5,
            },
        },
    },

    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0)',
        outline: '2px solid $primary04',
    },
})

const StyledNavButtonContainer = styled('div', {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 16px',
    pointerEvents: 'none',
    zIndex: 20,
})

const StyledToolbar = styled('div', {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    zIndex: 15,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '2px',
    borderRadius: '6px',
})

const ToolbarButton = ({ onClick, iconName, tooltipText }) => (
    <StyledZoomButton onClick={onClick} title={tooltipText}>
        <Icon name={iconName} iconSize="24px" css={{ color: 'white' }} />
    </StyledZoomButton>
)

const StyledZoomButton = styled('div', {
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transitionDuration: '0.2s',
    borderRadius: '4px',

    '&:hover': {
        backgroundColor: '$primary02',
    },
})

const StyledZoomContainer = styled('div', {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    userSelect: 'none',
    zIndex: 5,
})

const StyledAnnotationContainer = styled('div', {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 25,
    pointerEvents: 'none',
    '& > *': {
        pointerEvents: 'auto',
    },
})

/*
 * "Smart" wrapper for CommentFeedItem
 */
const CommentWrapper = ({ comment, onDelete }) => {
    const handleCommentEdit = (comment, newText) =>
        runCommand(CommentChangedCommand.Outbound(comment.id, { text: newText }))

    const isEditingAllowed = useSelector(ReduxSelectors.isUpdateAllowed('comment', comment))
    const { runCommand } = useCommandHistory()

    return (
        <CommentFeedItem
            createdAt={comment.createdAt}
            onDelete={() => onDelete(comment)}
            onSave={newText => handleCommentEdit(comment, newText)}
            participant={comment.createdBy}
            text={comment.text}
            isEditingAllowed={isEditingAllowed}
        />
    )
}

/*
 * (Possibly) show the previous and next buttons, either of which may be disabled if we at the start or end of the list
 */
const PreviousNextButtons = ({ isNavigationAllowed, handleUploadNavigationClicked }) => {
    const onNextClick = () => handleUploadNavigationClicked(1)
    const onPrevClick = () => handleUploadNavigationClicked(-1)

    const showNavigation = isNavigationAllowed.prev || isNavigationAllowed.next

    if (!showNavigation) return null // no buttons needed; there's nowhere to go

    return (
        <StyledNavButtonContainer>
            <StyledNavButton css={{ marginLeft: '16px' }} onClick={onPrevClick} disabled={!isNavigationAllowed.prev}>
                <Icon name="chevronLeft" iconSize="16px" css={{ color: 'white' }} />
            </StyledNavButton>
            <StyledNavButton css={{ marginRight: '16px' }} onClick={onNextClick} disabled={!isNavigationAllowed.next}>
                <Icon name="chevronRight" iconSize="16px" css={{ color: 'white' }} />
            </StyledNavButton>
        </StyledNavButtonContainer>
    )
}

const ZoomControls = ({ zoomLevel, setZoomLevel, resetPan, handleZoom }) => {
    const handleZoomIn = () => {
        handleZoom(0.2)
    }

    const handleZoomOut = () => {
        handleZoom(-0.2)
    }

    const handleReset = () => {
        setZoomLevel(1)
        resetPan()
    }

    return (
        <StyledToolbar css={{ bottom: '12px', left: '12px' }}>
            <ToolbarButton onClick={handleZoomIn} iconName="zoomIn" tooltipText="Zoom in" />
            <ToolbarButton onClick={handleReset} iconName="centerMap" tooltipText="Center image" />
            <ToolbarButton onClick={handleZoomOut} iconName="zoomOut" tooltipText="Zoom out" />
        </StyledToolbar>
    )
}

const ImageWithRef = forwardRef(
    ({ src, onLoad, isLoading, style, onMouseDown, onMouseMove, onMouseUp, onMouseLeave }, peerRef) => (
        <StyledImg
            id="photo-annotation-image"
            ref={peerRef}
            src={src}
            onLoad={onLoad}
            isLoading={isLoading}
            style={style}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            draggable={false}
        />
    )
)

/*
 * Container for the fullscreen image, handles loading and shows proper indicator
 */
const MediaDetailsImageContainer = ({
    isEditing,
    setIsEditing,
    imageUrl,
    currentUpload,
    isNavigationAllowed,
    handleUploadNavigationClicked,
}) => {
    const [isLoading, setIsLoading] = useState(true)
    const [zoomLevel, setZoomLevel] = useState(1)
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
    const containerRef = useRef(null)
    const imageRef = useRef()

    const resetPan = useCallback(() => setPanPosition({ x: 0, y: 0 }), [])

    const isZoomed = zoomLevel > 1

    const getImageStyle = useCallback(
        () => ({
            transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
            cursor: isZoomed ? 'move' : 'default',
            transformOrigin: 'center',
            transition: isDragging ? 'none' : 'transform 0.05s ease-out',
        }),
        [panPosition.x, panPosition.y, zoomLevel, isZoomed, isDragging]
    )

    // Reset zoom and pan when entering annotation mode
    useEffect(() => {
        if (isEditing === EDITING) {
            setZoomLevel(1)
            resetPan()
        }
    }, [isEditing, resetPan])

    const handleZoom = useCallback(
        (zoomDelta, cursorX, cursorY) => {
            if (!Upload.isImage(currentUpload) || isEditing === EDITING) return

            const maxZoom = 5
            const minZoom = 1
            const newZoomLevel = Math.max(minZoom, Math.min(maxZoom, zoomLevel + zoomDelta))

            if (newZoomLevel === minZoom) {
                resetPan()
                setZoomLevel(minZoom)
                return
            }

            setZoomLevel(newZoomLevel)

            // If we have cursor coordinates, zoom toward that point
            if (cursorX !== undefined && cursorY !== undefined && imageRef.current) {
                const rect = imageRef.current.getBoundingClientRect()
                const offsetX = (cursorX - rect.left) / rect.width - 0.5
                const offsetY = (cursorY - rect.top) / rect.height - 0.5

                const zoomRatio = newZoomLevel / zoomLevel
                setPanPosition(prev => ({
                    x: prev.x - offsetX * rect.width * (zoomRatio - 1),
                    y: prev.y - offsetY * rect.height * (zoomRatio - 1),
                }))
            }
        },
        [currentUpload, zoomLevel, resetPan, isEditing]
    )

    const handleWheel = useCallback(
        e => {
            if (!Upload.isImage(currentUpload) || isEditing === EDITING) return

            e.preventDefault()
            const direction = e.deltaY < 0 ? 1 : -1
            const step = 0.2

            handleZoom(direction * step, e.clientX, e.clientY)
        },
        [currentUpload, handleZoom, isEditing]
    )

    const handleMouseDown = useCallback(
        e => {
            if (!isZoomed || isEditing === EDITING) return

            setIsDragging(true)
            setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y })
            e.preventDefault()
        },
        [isZoomed, panPosition, isEditing]
    )

    const handleMouseMove = useCallback(
        e => {
            if (!isDragging || !isZoomed || isEditing === EDITING || !imageRef.current) return

            const newX = e.clientX - dragStart.x
            const newY = e.clientY - dragStart.y

            const container = containerRef.current
            const image = imageRef.current
            if (!container || !image) return

            const containerRect = container.getBoundingClientRect()
            const imageRect = image.getBoundingClientRect()

            const maxX = (imageRect.width * zoomLevel - containerRect.width) / 2
            const maxY = (imageRect.height * zoomLevel - containerRect.height) / 2

            setPanPosition({
                x: Math.max(-maxX, Math.min(maxX, newX)),
                y: Math.max(-maxY, Math.min(maxY, newY)),
            })

            e.preventDefault()
        },
        [isDragging, isZoomed, dragStart, zoomLevel, isEditing]
    )

    const handleMouseUp = useCallback(
        e => {
            if (isDragging) {
                setIsDragging(false)
                e.preventDefault()
            }
        },
        [isDragging]
    )

    const handleMouseLeave = useCallback(
        e => {
            if (isDragging) {
                setIsDragging(false)
                e.preventDefault()
            }
        },
        [isDragging]
    )

    // Add document-level mouse event listeners
    useEffect(() => {
        if (!isZoomed) return

        const handleGlobalMouseUp = () => {
            setIsDragging(false)
        }

        const handleGlobalMouseLeave = () => {
            setIsDragging(false)
        }

        document.addEventListener('mouseup', handleGlobalMouseUp)
        document.addEventListener('mouseleave', handleGlobalMouseLeave)

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp)
            document.removeEventListener('mouseleave', handleGlobalMouseLeave)
        }
    }, [isZoomed])

    const handleImageLoad = useCallback(() => {
        setIsLoading(false)
    }, [])

    // Set up wheel event listener
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        container.addEventListener('wheel', handleWheel, { passive: false })
        return () => container.removeEventListener('wheel', handleWheel)
    }, [handleWheel])

    // Reset state when image changes
    useEffect(() => {
        setZoomLevel(1)
        resetPan()
        setIsLoading(true)
        setIsDragging(false)
        setDragStart({ x: 0, y: 0 })
    }, [imageUrl, resetPan])

    const _setIsEditing = v => {
        if (v === NEWLY_SAVED) {
            setIsLoading(true)
            v = NOT_EDITING
        }
        setIsEditing(v)
    }

    return (
        <Flex
            ref={containerRef}
            align="center"
            css={{ width: '100%', backgroundColor: '$snapshotBackground', position: 'relative' }}
        >
            {isLoading && <LoadingDotsAnimation />}
            <StyledZoomContainer>
                <ImageWithRef
                    ref={imageRef}
                    src={imageUrl}
                    onLoad={handleImageLoad}
                    isLoading={isLoading}
                    style={getImageStyle()}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    draggable={false}
                />
            </StyledZoomContainer>
            {isEditing !== EDITING && (
                <PreviousNextButtons
                    isNavigationAllowed={isNavigationAllowed}
                    handleUploadNavigationClicked={handleUploadNavigationClicked}
                />
            )}
            {!isLoading && Upload.isImage(currentUpload) && isEditing === NOT_EDITING && (
                <ZoomControls
                    zoomLevel={zoomLevel}
                    setZoomLevel={setZoomLevel}
                    resetPan={resetPan}
                    handleZoom={handleZoom}
                />
            )}
            <StyledAnnotationContainer>
                {!isLoading && Upload.isImage(currentUpload) && (
                    <PhotoAnnotationEditor
                        isEditing={isEditing}
                        setIsEditing={_setIsEditing}
                        imageRef={imageRef}
                        disabled={isLoading}
                        upload={currentUpload}
                    />
                )}
            </StyledAnnotationContainer>
        </Flex>
    )
}

const DownloadButton = ({ url, name, fileType }) => {
    const onClick = () => downloadImage(url, name + '.' + fileType)

    const css = { position: 'absolute', bottom: 40, br: 14, p: 16, bg: '#1D1E29', border: '#333333' }
    return (
        <Box css={css}>
            <Button variant="primary" size="xl" onClick={onClick}>
                <Icon name="download" iconSize="16px" />
                Download
            </Button>
        </Box>
    )
}

/*
 * Show an image or a "preview available" depending on the type of the upload
 */
const ContentViewer = ({
    isEditing,
    setIsEditing,
    imageUrl,
    currentUpload,
    isNavigationAllowed,
    handleUploadNavigationClicked,
}) => {
    const params = useSelector(ReduxSelectors.paramsForTrackEvent)
    const selectedProject = useSelector(ReduxSelectors.selectedProject)
    Segment.sendTrack(
        'media viewed',
        currentUpload.id,
        mergeRight(params, { mediaType: currentUpload.fileType, projectName: selectedProject.name })
    )

    // show image
    if (Upload.isImage(currentUpload))
        return (
            <MediaDetailsImageContainer
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                currentUpload={currentUpload}
                imageUrl={imageUrl}
                isNavigationAllowed={isNavigationAllowed}
                handleUploadNavigationClicked={handleUploadNavigationClicked}
            />
        )

    const { fileType, fileSize, description, name } = currentUpload

    // show "no preview available"
    return (
        <Flex align="center" css={{ width: '100%', backgroundColor: '$snapshotBackground', position: 'relative' }}>
            <MediaIconFullscreen fileType={fileType} fileSize={fileSize} description={description} name={name} />
            <PreviousNextButtons
                handleUploadNavigationClicked={handleUploadNavigationClicked}
                isNavigationAllowed={isNavigationAllowed}
            />
            <DownloadButton url={imageUrl} name={name} fileType={fileType} />
        </Flex>
    )
}

/*
 * Compute whether the previous/next buttons should be allowed
 *
 * In the Media View the top-left upload has the LOWEST index
 * In the CollaborationWindow the top-most upload has the HIGHEST index
 *
 * So for the Media View, there is no PREV if we're at 0 but for the Collaboration Window there is no NEXT if we're at 0
 * Likewise when we click, the direction delta has to fit. For the Media View going RIGHT is an INCREASE in the index
 * while for the Collaboration Window going RIGHT is a DECREASE in the index
 *
 * See handleUploadNavigationClicked too
 */
const computePrevAndNext = (currentIndex, allIndexes) => {
    const navigatedFrom = window.history.state?.usr?.navigatedFrom

    const isIndexBelowMax = currentIndex >= 0 && currentIndex < allIndexes.length - 1
    const isIndexAbove0 = currentIndex > 0

    return navigatedFrom !== 'collaborationWindow'
        ? { prev: isIndexAbove0, next: isIndexBelowMax } // Media View: "normal" direction
        : { prev: isIndexBelowMax, next: isIndexAbove0 } // Collaboration: "reverse" direction
}

const NOT_EDITING = PhotoAnnotationEditor.NOT_EDITING
const EDITING = PhotoAnnotationEditor.EDITING
const NEWLY_SAVED = PhotoAnnotationEditor.NEWLY_SAVED

/*
 * Display a full size image for an upload, together with a sidebar containing information about
 * author, date of creation, description, tags and a section for comments related to this upload.
 * Allows the user to add, modify and delete those upload fields.
 */
const MediaDetailsView = () => {
    const styledMediaDetailsViewSidebar = (currentUpload, project) => {
        const textAreaCss = {
            border: 'none',
            width: '100%',
            background: 'none',
            br: '0px',
            marginBottom: '20px',
            fw: 500,

            '&:hover': { border: 'none' },
            '&:active': { border: 'none' },
            '&:focus': { border: 'none' },
            '&::placeholder': { border: 'none', color: '$neutral05' },
        }

        const topFlexCss = {
            padding: '24px',
            backgroundColor: '$neutral10',
            borderBottom: '1px solid $neutral07',
        }

        const bottomFlexCss = {
            padding: '24px',
            gap: '16px',
        }

        const { collaborationIdentifier } = currentUpload

        const identifierComponent = collaborationIdentifier && (
            <Identifier
                projectIdentifier={selectedProject?.identifier}
                collaborationIdentifier={collaborationIdentifier}
                variant="small"
            />
        )

        const navigateToUpload = (uploadId, isNewUpload = false) => {
            // If we navigate to freshly added upload we need to add it to the uploadsIdsArray,
            // so arrow navigation between uploads works correctly
            const uploadIds = isNewUpload ? [uploadId].concat(uploadIdsInContext) : uploadIdsInContext

            navigate(`../${uploadId}`, {
                state: {
                    safeToNavigateBack: true,
                    uploadIds,
                    navigatedFrom: 'mediaDetailsView',
                    navigatedFromUrl: window.location.pathname,
                },
            })
        }

        const handleUploadNewFile = e => {
            const file = e.target.files[0]
            addUpload(file)
        }

        const addUpload = async file => {
            try {
                const collaboration = ReduxSelectors.collaborationForUpload(getState(), currentUpload)
                const id = v4()
                const imageHeightToWidth = Upload.isImageFile(file) ? await getImageHeightToWidth(file) : 1
                const upload = Upload.uploadForCollaboration({
                    id,
                    collaboration,
                    userId,
                    name: Upload.dropExtension(file.name),
                    imageHeightToWidth,
                    fileType: Upload.guessFileType(file),
                    fileSize: file.size,
                    correlationId: currentUpload.correlationId,
                })

                runCommand(UploadAddedCommand.Outbound(upload, file))
                navigateToUpload(id, true) // navigate to the upload page
            } catch (e) {
                console.error(e)
                alert(`Error while uploading file: ${file.name}`)
            }
        }

        const onUploadDelete = () => {
            closeDetailsView()
            runCommand(UploadRemovedCommand.Outbound(currentUpload.id))
        }

        const callDeleteUploadModal = () => {
            const documentType = Upload.isImage(currentUpload) ? 'photo' : 'document'

            const title = `Are you sure you want to delete this ${documentType}?`
            const description = `You cannot undo this action.`
            dispatch(
                ReduxActions.globalModalDataSet({
                    type: 'destructive',
                    title,
                    description,
                    cancelButton: {
                        label: 'Cancel',
                    },
                    submitButton: {
                        label: 'Delete',
                        onClick: onUploadDelete,
                    },
                })
            )
        }

        const onDeleteComment = comment => runCommand(CommentRemovedCommand.Outbound(comment.isNote, comment.id))

        const callDeleteCommentModal = comment => {
            const title = `Are you sure you want to delete this comment?`
            const description = `You cannot undo this action.`
            dispatch(
                ReduxActions.globalModalDataSet({
                    type: 'destructive',
                    title,
                    description,
                    cancelButton: {
                        label: 'Cancel',
                    },
                    submitButton: {
                        label: 'Delete',
                        onClick: () => onDeleteComment(comment),
                    },
                })
            )
        }

        return (
            <StyledMediaDetailsViewSidebar>
                <MediaDetailsViewTopBar
                    upload={currentUpload}
                    allowDelete={allowUploadDelete}
                    uploadsLimitReached={uploadsLimitReached}
                    onCloseClick={closeDetailsView}
                    onDeleteClick={callDeleteUploadModal}
                    onDownloadClick={onDownloadClick}
                    onUploadClicked={navigateToUpload}
                    onUploadNewFile={handleUploadNewFile}
                />
                <StyledSidebarContent shouldShrink={isCommentBoxFocused}>
                    <StyledSidebarContentViewport>
                        <FlexColumn css={topFlexCss}>
                            <FlexRow>
                                <MediaDetailsViewLocationTile
                                    {...currentUpload}
                                    onClick={handleLocationSnapshotClicked}
                                    identifierComponent={identifierComponent}
                                />
                            </FlexRow>
                            <TextAreaExternallyChangeable
                                placeholder={`Add a description...\n(Any text added here will appear in the PDF photo reports)`}
                                externalValue={currentUpload.description ?? null}
                                onBlur={handleDescriptionChange}
                                maxLineCount={20}
                                css={textAreaCss}
                            />
                            <Tags tags={currentUpload?.tagIds ?? []} onTagsChanged={handleTagsChange} />
                        </FlexColumn>
                        <FlexColumn css={bottomFlexCss}>
                            {comments.map(c => (
                                <CommentWrapper
                                    comment={c}
                                    key={`comment-feed-item-${c.id}`}
                                    onDelete={callDeleteCommentModal}
                                />
                            ))}
                        </FlexColumn>
                        <StyledSidebarContentScrollbar orientation="vertical">
                            <StyledSidebarContentScrollbarThumb />
                        </StyledSidebarContentScrollbar>
                    </StyledSidebarContentViewport>
                </StyledSidebarContent>
                <CommentBox
                    onCommentSubmit={handleCommentAdded}
                    onFocusChange={isFocused => setIsCommentBoxFocused(isFocused)}
                    userAvatarUrl={participant.avatarUrl}
                    userName={Participant.fullName(participant)}
                    css={{ backgroundColor: '$neutral10' }}
                />
            </StyledMediaDetailsViewSidebar>
        )
    }
    // "upload" here is an enhanced version of an Upload object with extra fields to simplify presentation
    const enhanceUpload = upload => {
        const participant = ReduxSelectors.selectedProjectParticipantWithId(getState(), upload.createdById)
        const parentCollaboration = allCollaborations[upload.parentId]
        const geometry = ReduxSelectors.geometryForCollaboration(getState(), parentCollaboration)
        const canvas = allCanvases[geometry.canvasId]
        const isTask = !!Collaboration.isTask(parentCollaboration)
        const mapPinType = isTask ? 'task' : 'photo'
        const isArchived = !!geometry?.archivedDate
        const statusName = ReduxSelectors.statusNameForCollaboration(getState(), parentCollaboration)
        const isCompleted = !!statusName?.isCompleted
        const collaboration = ReduxSelectors.collaborationForUpload(getState(), upload)
        const correlatedUploads = ReduxSelectors.correlatedUploads(getState(), upload)
        const version = correlatedUploads.length > 1 ? correlatedUploads.indexOf(upload) : null // only add versioning if there is more than one correlated upload
        const category = ReduxSelectors.categoryForCollaboration(getState(), parentCollaboration)

        return mergeRight(upload, {
            canvasId: canvas.id,
            collaborationName: collaboration?.name,
            collaborationTagNames: ReduxSelectors.textsFromTagNameIds(getState(), parentCollaboration.tagNameIds),
            collaborationUploadCount: ReduxSelectors.uploadsForCollaborationId(getState(), parentCollaboration.id)
                .length,
            collaborationIdentifier: collaboration?.identifier,
            isArchived,
            isCompleted,
            isTask,
            locationSnapshotUrl,
            locationLabel: canvas.name,
            mapPinType,
            name: Upload.isImage(upload) ? undefined : upload.name, // names for images are useless here
            statusNameColor: statusName?.color,
            tagNames: ReduxSelectors.textsFromTagNameIds(getState(), upload.tagIds),
            participant,
            version,
            correlatedUploads,
            category,
        })
    }

    const handleCommentAdded = newCommentText => {
        const comment = Comment.commentForUpload(currentUpload, userId, newCommentText)
        return runCommand(CommentAddedCommand.Outbound(comment))
    }

    const handleDescriptionChange = description => {
        if (description !== currentUpload.description)
            runCommand(UploadChangedCommand.Outbound(currentUpload.id, { description }))
    }

    const handleTagsChange = tags => {
        const tagIds = tags.map(tag => tag.id)
        runCommand(UploadChangedCommand.Outbound(currentUpload.id, { tagIds }))
    }

    const closeDetailsView = () => {
        const navigatedFrom = window.history.state?.usr?.navigatedFrom
        if (navigatedFrom === 'collaborationWindow') {
            const navigatedFromUrl = window.history.state?.usr?.navigatedFromUrl
            navigate(navigatedFromUrl)
        } else if (navigatedFrom === 'mediaView') {
            // when the user came here from MediaView and closes the window we also want to restore the scroll state
            const navigatedFromUrl = window.history.state?.usr?.navigatedFromUrl
            const restoreScrollState = window.history.state?.usr?.scrollState
            navigate(navigatedFromUrl, { state: { restoreScrollState } })
        } else navigate('..')
    }

    const handleUploadNavigationClicked = dirDelta => {
        const navigatedFrom = window.history.state?.usr?.navigatedFrom
        const navigatedFromUrl = window.history.state?.usr?.navigatedFromUrl
        const scrollState = window.history.state?.usr?.scrollState

        // see comment for computePrevAndNext; we need to change the direction of the index
        if (navigatedFrom === 'collaborationWindow') dirDelta = -dirDelta

        const uploadIndexToNavigate = currentUploadIndexInContext + dirDelta
        navigate(`../${uploadIdsInContext[uploadIndexToNavigate]}`, {
            state: {
                safeToNavigateBack: false,
                uploadIds: uploadIdsInContext,
                navigatedFrom,
                navigatedFromUrl,
                scrollState,
            },
        })
    }

    const handleLocationSnapshotClicked = () => navigateToCollaboration(currentUpload.parentId)

    const onDownloadClick = () => downloadImage(url, Upload.fileName(currentUpload))

    // main
    const { runCommand } = useCommandHistory()
    const { pushKeyMap, popKeyMap } = useKeyMaps()
    const { dispatch, getState } = useStore()
    const { uploadId } = useParams()
    const navigate = useNavigate()
    const { navigateToCollaboration } = useNavigateToCollaboration()
    const [currentUpload, setCurrentUpload] = useState()
    const [isCommentBoxFocused, setIsCommentBoxFocused] = useState(false)
    const [isEditing, setIsEditing] = useState(NOT_EDITING)
    const { url } = useImageUrl(
        isEditing === NOT_EDITING ? URL_SEARCH_ORDER.ANNOTATED : URL_SEARCH_ORDER.MAIN, // hide the "live" annotations?
        currentUpload?.id
    )
    const { url: locationSnapshotUrl } = useImageUrl(URL_SEARCH_ORDER.LOCATION_THUMBNAIL_1, currentUpload?.parentId)

    const allUploads = useSelector(ReduxSelectors.uploadsAsPOJO)
    const allCollaborations = useSelector(ReduxSelectors.collaborationsAsObject)
    const allCanvases = useSelector(ReduxSelectors.canvasesAsPOJO)
    const userId = useSelector(ReduxSelectors.selectedUserId)
    const selectedProject = useSelector(ReduxSelectors.selectedProject)
    const comments = useSelector(() => ReduxSelectors.commentsForUpload(getState(), currentUpload))
    const allowUploadDelete = useSelector(ReduxSelectors.isUpdateAllowed('upload', currentUpload))

    const uploadIdsInContext = window.history?.state?.usr?.uploadIds ?? []
    const currentUploadIndexInContext = uploadIdsInContext.findIndex(id => id === uploadId)
    const isNavigationAllowed = computePrevAndNext(currentUploadIndexInContext, uploadIdsInContext)
    const participant = ReduxSelectors.selectedProjectParticipantWithId(getState(), userId)
    const organization = useSelector(ReduxSelectors.selectedOrganization)

    const uploadsLimitReached = useMemo(() => {
        if (!organization.limitsAndCounts) return false
        const { uploadStorageBytesCount, uploadStorageBytesLimit } = organization.limitsAndCounts
        return uploadStorageBytesCount >= uploadStorageBytesLimit
    }, [organization])

    useEffect(() => {
        const uploadToDisplay = allUploads[uploadId]
        if (uploadToDisplay) {
            setCurrentUpload(enhanceUpload(uploadToDisplay))
        }
    }, [allUploads, uploadId, locationSnapshotUrl])

    useEffect(() => {
        pushKeyMap('MediaDetailsView', { Escape: () => closeDetailsView() })
        if (window.history.state?.usr?.startWithAnnotations) setIsEditing(EDITING)
        return () => popKeyMap('MediaDetailsView')
    }, [])

    if (!selectedProject) return null // probably had access to the project revoked

    return (
        <StyledMediaDetailsViewContainer data-cy="media-details-view">
            {currentUpload && (
                <>
                    <ContentViewer
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        imageUrl={url}
                        currentUpload={currentUpload}
                        isNavigationAllowed={isNavigationAllowed}
                        handleUploadNavigationClicked={handleUploadNavigationClicked}
                    />

                    {isEditing === NOT_EDITING && styledMediaDetailsViewSidebar(currentUpload, selectedProject)}
                </>
            )}
        </StyledMediaDetailsViewContainer>
    )
}

export default MediaDetailsView
